import {
	Grid,
	Typography,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
} from '@mui/material';

import { useEffect, useState, ChangeEvent, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as ExpandMoreRoundedIcon } from 'common/assets/images/select-chevron.svg';
import RangeSlider from 'common/components/RangeSlider/RangeSlider';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import { RootState } from 'common/store/AppStore';
import {
	setPrompt,
	setMessages,
	setSystemText,
} from 'common/store/reducer/promptBuilderReducer';
import { toSentenceCase } from 'common/utils/utilFunctions';

import {
	IRangeSliderObject,
	IPromptProps,
	IAddMessages,
	IConfigurePane,
} from '../../api/type-def';
import {
	createRangeSliderArray,
	disableSaveHandler,
	restructureMessagesArray,
	getModifiedStoreData,
} from '../../PromptBuilderUtils';

import {
	MESSAGE_OPTIONS,
	rangeSliderObject,
	INDUSTRY_OPTIONS,
	CATEGORY_OPTIONS,
} from '../PromptBuilderConstants';
import PromptLayout from '../PromptLayout/PromptLayout';
import TestPane from '../TestPane/TestPane';

import styles from './ConfigurePane.module.scss';

const ConfigurePane = (props: IConfigurePane) => {
	/* Destructured variables */
	const { isEdit, isTestPane, testPaneHandler, onPromptChangeHandler } = props;

	const { SYSTEM } = MESSAGE_OPTIONS;

	const { iconFill, iconWidth, iconSpacing, textColor } = styles;

	const inputRef = useRef<HTMLInputElement | null>(null);

	const existingPromptDetailsFromStore: any = useSelector(
		(state: RootState) => state.promptBuilder.existingPromptDetails,
	);

	const prompt: IPromptProps = useSelector(
		(state: RootState) => state.promptBuilder.prompt,
	);

	const messages: IAddMessages[] | [] = useSelector(
		(state: RootState) => state.promptBuilder.messages,
	);

	const systemText: string = useSelector(
		(state: RootState) => state.promptBuilder.systemText,
	);

	const detailPaneTabIndex = useSelector(
		(state: RootState) => state.promptBuilder.detailPaneIndex,
	);

	const { systemMsg } = getModifiedStoreData(existingPromptDetailsFromStore);

	const dispatch = useDispatch();

	const getExistingData = () => {
		const { existingDetails } = getModifiedStoreData(
			existingPromptDetailsFromStore,
		);
		return existingDetails;
	};

	const existingPromptData = getExistingData();
	const [isAddClicked, setIsAddClicked] = useState<boolean>(false);
	const [categoryShrink, setCategoryShrink] = useState(false);
	const [industryShrink, setIndustryShrink] = useState(false);
	const [shrinkSequences, setShrinkSequences] = useState(false);
	const [enableInitialClick, setEnableInitialClick] = useState(false);

	/* Local variables */
	const rangeSliderArray = createRangeSliderArray(rangeSliderObject);
	const selectStyle = {
		'& .MuiSelect-select.MuiInputBase-input.MuiFilledInput-input:focus': {
			backgroundColor: 'transparent',
			width: '100%',
		},
		'& .MuiSelect-icon': {
			width: iconWidth,
			marginRight: iconSpacing,
			fill: iconFill,
		},
	};

	const formControlStyle = {
		'& .MuiFilledInput-underline:before': {
			borderBottom: 'none',
		},
		'& .MuiFilledInput-underline:after': {
			borderBottom: 'none',
			color: textColor,
		},
		'&:hover': {
			'& .MuiFilledInput-underline:before': {
				borderBottom: 'none',
			},
		},
	};

	/**
	 * @description method to set the max length on change
	 * @param event
	 */
	const onMaxLengthChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
		dispatch(
			setPrompt({
				...prompt,
				parameters: {
					...prompt?.parameters,
					max_tokens: Number(event?.target?.value),
				},
			}),
		);
	};

	/**
	 * @description method to set the industry on change
	 * @param event
	 */
	const onIndustryChangeHandler = (event: SelectChangeEvent<string>) => {
		dispatch(
			setPrompt({
				...prompt,
				parameters: {
					...prompt?.parameters,
					industry: event?.target?.value,
				},
			}),
		);
	};

	/**
	 * @description method to set the category on change
	 * @param event
	 */
	const onCategoryChangeHandler = (event: SelectChangeEvent<string>) => {
		dispatch(
			setPrompt({
				...prompt,
				parameters: {
					...prompt?.parameters,
					category: event?.target?.value,
				},
			}),
		);
	};

	/**
	 * @description method to set the system message text on change
	 * @param event
	 */
	const onSystemMsgChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
		dispatch(setSystemText(event?.target?.value));
	};

	/**
	 * @description method to set the user text on change
	 * @param id, event
	 */
	const onUserChangeHandler = (
		id: number,
		event: ChangeEvent<HTMLInputElement>,
	) => {
		const updatedElements = messages?.map((messages) =>
			messages.id === id ? { ...messages, user: event.target.value } : messages,
		);
		dispatch(setMessages(updatedElements));
	};

	/**
	 * @description method to set the assistant text on change
	 * @param id, event
	 */
	const onAssistantChangeHandler = (
		id: number,
		event: ChangeEvent<HTMLInputElement>,
	) => {
		const updatedElements = messages?.map((messages) =>
			messages.id === id
				? { ...messages, assistant: event?.target?.value }
				: messages,
		);
		dispatch(setMessages(updatedElements));
	};

	/**
	 * @description method to add the messages
	 * @param isClicked
	 */
	const onAddBtnClickHandler = (isClicked: boolean) => {
		setIsAddClicked(isClicked);
		const newElement: IAddMessages = {
			id: messages.length + 1,
			user: '',
			assistant: '',
		};
		dispatch(setMessages([...messages, newElement]));
	};

	/**
	 * @description method to delete the messages
	 * @param msg
	 */
	const onPromptDeleteHandler = (msg: IAddMessages) => {
		const updatedMsg = messages?.filter((obj) => obj?.id !== msg?.id);
		dispatch(setMessages(updatedMsg));
	};

	/* To restructure the messages array */
	useEffect(() => {
		const messageArray = restructureMessagesArray(messages);
		const messageSystem = { content: systemText, role: SYSTEM };
		const newArray = [messageSystem, ...messageArray];
		dispatch(
			setPrompt({
				...prompt,
				messages: systemText || systemMsg ? newArray : messageArray,
			}),
		);
	}, [messages, systemText]);

	useEffect(() => {
		if (isEdit && inputRef.current && !enableInitialClick) {
			inputRef.current.click();
			setEnableInitialClick(true);
		}
		const industry = prompt?.parameters?.industry;
		const category = prompt?.parameters?.category;

		if (industry !== '') {
			setIndustryShrink(true);
		}
		if (category !== '') {
			setCategoryShrink(true);
		}
	}, [prompt, detailPaneTabIndex]);

	const getDefaultRangeValues = (id: keyof IPromptProps['parameters']): any => {
		const value = prompt?.parameters?.[id];
		if (Array.isArray(value)) {
			return [...value];
		}
		return value;
	};
	useEffect(() => {
		testPaneHandler(
			disableSaveHandler(prompt, isEdit, systemText, existingPromptData),
		);
	}, [prompt, isEdit, existingPromptData]);

	return (
		<div className={styles['configure-pane-container']}>
			<Grid container spacing={3}>
				<Grid item xs={8.5} lg={8.5} md={8.5} sm={8.5}>
					{isTestPane ? (
						<TestPane prompt={prompt} />
					) : (
						<PromptLayout
							onPromptChangeHandler={onPromptChangeHandler}
							onSystemMsgChangeHandler={onSystemMsgChangeHandler}
							onUserChangeHandler={onUserChangeHandler}
							prompt={prompt}
							onAssistantChangeHandler={onAssistantChangeHandler}
							messages={messages}
							onAddBtnClickHandler={onAddBtnClickHandler}
							isAddClicked={isAddClicked}
							systemText={systemText}
							onPromptDeleteHandler={onPromptDeleteHandler}
						/>
					)}
				</Grid>
				<Grid item xs={3.5} lg={3.5} md={3.5} sm={3.5}>
					<Typography className={styles['sub-header-text']}>
						{toSentenceCase('Parameters')}
					</Typography>
					<FormControl
						variant="filled"
						className={styles['type-formcontrol']}
						sx={formControlStyle}
					>
						<InputLabel
							className={styles['type-label']}
							data-testid="type-label"
							shrink={industryShrink}
						>
							{toSentenceCase('Industry')}
						</InputLabel>
						<Select
							ref={inputRef}
							className={styles['select-item']}
							onChange={(e) => onIndustryChangeHandler(e)}
							value={prompt?.parameters?.industry}
							IconComponent={ExpandMoreRoundedIcon}
							inputProps={{
								'data-testid': 'industry-dropdown',
							}}
							sx={selectStyle}
							onFocus={() => {
								if (prompt?.parameters?.industry !== '')
									setIndustryShrink(true);
							}}
							data-testid="type-select"
							readOnly={!prompt?.is_draft}
						>
							{Object.entries(INDUSTRY_OPTIONS).map(([key, value]) => (
								<MenuItem key={key} value={value}>
									{value}
								</MenuItem>
							))}{' '}
						</Select>
					</FormControl>
					<FormControl
						variant="filled"
						className={styles['type-formcontrol']}
						sx={formControlStyle}
					>
						<InputLabel
							className={styles['type-label']}
							data-testid="type-label"
							shrink={categoryShrink}
						>
							{toSentenceCase('Category')}
						</InputLabel>
						<Select
							ref={inputRef}
							className={styles['select-item']}
							onChange={(e) => onCategoryChangeHandler(e)}
							value={prompt?.parameters?.category}
							IconComponent={ExpandMoreRoundedIcon}
							inputProps={{
								'data-testid': 'category-dropdown',
							}}
							sx={selectStyle}
							onFocus={() => {
								if (prompt?.parameters?.category !== '')
									setCategoryShrink(true);
							}}
							data-testid="type-select"
							readOnly={!prompt?.is_draft}
						>
							{Object.values(CATEGORY_OPTIONS).map((category) => (
								<MenuItem key={category} value={category}>
									{category}
								</MenuItem>
							))}{' '}
						</Select>
					</FormControl>
					<RangeSlider
						minRange="0.0"
						maxRange="1.0"
						title="Temperature"
						steps={0.01}
						defaultValue={Number(prompt?.parameters?.temperature)}
						readonly={!prompt?.is_draft}
						disabled={!prompt?.is_draft}
						onRangeValueChange={(value: number) =>
							dispatch(
								setPrompt({
									...prompt,
									parameters: {
										...prompt.parameters,
										temperature: value,
									},
								}),
							)
						}
					/>
					<TextFieldWithLabel
						label={toSentenceCase('Max Length (Tokens)')}
						inputProps={{
							'data-testid': 'max-length-token',
						}}
						error={false}
						value={prompt?.parameters?.max_tokens}
						onChange={onMaxLengthChangeHandler}
						helperText=""
						showLabel={false}
						type="number"
						className={styles['text-field-style']}
						isFilled
						readOnly={!prompt?.is_draft}
					/>
					<FormControl
						variant="filled"
						className={styles['type-formcontrol']}
						sx={formControlStyle}
						disabled
					>
						<InputLabel
							className={styles['type-label']}
							data-testid="stop-sequences-label"
							shrink={shrinkSequences}
						>
							{toSentenceCase('Stop Sequences')}
						</InputLabel>
						<Select
							className={styles['select-item']}
							onChange={() => {}}
							value=""
							IconComponent={ExpandMoreRoundedIcon}
							inputProps={{
								'data-testid': 'stop-sequences-dropdown',
							}}
							sx={selectStyle}
							onFocus={() => {
								setShrinkSequences(true);
							}}
						>
							<MenuItem value="option" data-testid="option">
								Option
							</MenuItem>
						</Select>
					</FormControl>
					{rangeSliderArray?.map((rangeItem: IRangeSliderObject) => (
						<div key={rangeItem?.title}>
							<RangeSlider
								minRange={rangeItem.minRange}
								maxRange={rangeItem.maxRange}
								title={toSentenceCase(rangeItem.title)}
								defaultValue={getDefaultRangeValues(rangeItem?.id)}
								readonly={!prompt?.is_draft}
								disabled={!prompt?.is_draft}
								onRangeValueChange={(value: number) =>
									dispatch(
										setPrompt({
											...prompt,
											parameters: {
												...prompt.parameters,
												[rangeItem?.id]: value,
											},
										}),
									)
								}
								steps={rangeItem.steps}
							/>
						</div>
					))}
				</Grid>
			</Grid>
		</div>
	);
};

export default ConfigurePane;
